<template>
  <span>
    <v-row v-for="chart in chartDataComputed" :key="chart.title">
      <v-col cols="12">
        <v-card-charts-dashboard
          :title="chart.title"
          :options="chart.options"
          :deep-copy-on-update="false"
          :is-loading="chart.loading"
        />
      </v-col>
    </v-row>
  </span>
</template>

<script>
import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
HighchartsNoData(Highcharts);
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import { mapState } from "vuex";
import DashboardCobrancasIrregularidadesService from "@/services/DashboardCobrancasIrregularidades";
import dayjs from "dayjs";
import VCardChartsDashboard from "@/components/base/VCardChartsDashboard.vue";

exporting(Highcharts);
exportData(Highcharts);

export default {
  name: "DashboardCobrancasIrregularidadesVisaoGeral",
  components: {
    VCardChartsDashboard,
  },
  data() {
    return {
      dadosAcumulados: [],
      dadosMensais: [],
      chartData: [
        {
          title: "Percentual de TOIs com Cobrança (%)",
          chartOptionsKey: "graficoPercentualTOIs",
        },
        {
          title:
            "Quantidade de Inspeções (Verificação de Processamentos Irregulares)",
          chartOptionsKey: "graficoQuantidadeInspecoes",
        },
        {
          title:
            "Quantidade de TOIs com Cobrança por Procedimento Irregular Comprovado",
          chartOptionsKey: "graficoTOIsPorProcedimentoIrregular",
        },
        {
          title: "Quantidade de TOIs com Cobrança por Deficiência na Medição",
          chartOptionsKey: "graficoTOIsPorDeficienciaMedicao",
        },
      ],
      loadingPorChave: {
        dadosAcumulados: false,
        dadosMensais: false,
      },
    };
  },
  props: {
    activatedTab: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapState("parametrosDashboardCobrancasIrregularidades", [
      "competencia",
      "competenciaAte",
      "regionalSelecionada",
      "visaoGraficos",
      "quantidadeMunicipios",
    ]),
    chartDataComputed() {
      const loadingKey =
        this.visaoGraficos === "acumulados" ? "dadosAcumulados" : "dadosMensais";
      const isLoading = !!this.loadingPorChave[loadingKey];
      return this.chartData.map((chart) => ({
        ...chart,
        loading: isLoading,
        options: this[chart.chartOptionsKey],
      }));
    },
    dadosProcessados() {
      const dados =
        this.visaoGraficos === "acumulados"
          ? this.dadosAcumulados
          : this.dadosMensais;

      const meses = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];

      const categorias = meses.map((mes) => mes);

      const competenciaSelecionada = this.competencia;
      const anoAtual = dayjs(competenciaSelecionada).format("YYYY");
      const anoAnterior = dayjs(competenciaSelecionada)
        .subtract(1, "year")
        .format("YYYY");

      let processarDadosPorAno;

      if (this.visaoGraficos === "acumulados") {
        processarDadosPorAno = (ano) => {
          return meses.map((mes, index) => {
            const mesNumero = index + 1;
            return dados[ano]?.[mesNumero] || null;
          });
        };
      } else {
        processarDadosPorAno = (ano) => {
          return meses.map((mes, index) => {
            const mesNumero = index + 1;
            const competencia = `${mesNumero}/${ano}`;
            return dados[competencia] || null;
          });
        };
      }

      const dadosAnoAnterior = processarDadosPorAno(anoAnterior);
      const dadosAnoAtual = processarDadosPorAno(anoAtual);

      const percentualTOIs = {
        anoAnterior: dadosAnoAnterior.map(
          (d) => (d?.percentual_tois || 0) * 100,
        ),
        anoAtual: dadosAnoAtual.map((d) => (d?.percentual_tois || 0) * 100),
      };

      const inspecoes = {
        anoAnterior: dadosAnoAnterior.map((d) => d?.qtd_inspecoes || 0),
        anoAtual: dadosAnoAtual.map((d) => d?.qtd_inspecoes || 0),
      };

      const toisProcedimentoIrregular = {
        anoAnterior: dadosAnoAnterior.map((d) => d?.qtd_tois_cobradas || 0),
        anoAtual: dadosAnoAtual.map((d) => d?.qtd_tois_cobradas || 0),
      };

      const toisDeficienciaMedicao = {
        anoAnterior: dadosAnoAnterior.map((d) => d?.qtd_tois || 0),
        anoAtual: dadosAnoAtual.map((d) => d?.qtd_tois || 0),
      };

      return {
        categorias,
        percentualTOIs,
        inspecoes,
        toisProcedimentoIrregular,
        toisDeficienciaMedicao,
        anoAnterior,
        anoAtual,
      };
    },
    graficoPercentualTOIs() {
      return this.criarGraficoGenerico({
        series: [
          {
            name: `${this.dadosProcessados.anoAtual}`,
            data: this.dadosProcessados.percentualTOIs.anoAtual,
          },
          {
            name: `${this.dadosProcessados.anoAnterior}`,
            data: this.dadosProcessados.percentualTOIs.anoAnterior,
          },
        ],
        tituloY: "Percentual (%)",
        valueSuffix: "%",
      });
    },
    graficoQuantidadeInspecoes() {
      return this.criarGraficoGenerico({
        series: [
          {
            name: `${this.dadosProcessados.anoAtual}`,
            data: this.dadosProcessados.inspecoes.anoAtual,
          },
          {
            name: `${this.dadosProcessados.anoAnterior}`,
            data: this.dadosProcessados.inspecoes.anoAnterior,
          },
        ],
        tituloY: "Quantidade de Inspeções",
      });
    },
    graficoTOIsPorProcedimentoIrregular() {
      return this.criarGraficoGenerico({
        series: [
          {
            name: `${this.dadosProcessados.anoAtual}`,
            data: this.dadosProcessados.toisProcedimentoIrregular.anoAtual,
          },
          {
            name: `${this.dadosProcessados.anoAnterior}`,
            data: this.dadosProcessados.toisProcedimentoIrregular.anoAnterior,
          },
        ],
        tituloY: "Quantidade de TOIs",
      });
    },
    graficoTOIsPorDeficienciaMedicao() {
      return this.criarGraficoGenerico({
        series: [
          {
            name: `${this.dadosProcessados.anoAtual}`,
            data: this.dadosProcessados.toisDeficienciaMedicao.anoAtual,
          },
          {
            name: `${this.dadosProcessados.anoAnterior}`,
            data: this.dadosProcessados.toisDeficienciaMedicao.anoAnterior,
          },
        ],
        tituloY: "Quantidade de TOIs",
      });
    },
  },
  watch: {
    competencia() {
      this.getDados();
    },
    competenciaAte() {
      this.getDados();
    },
    regionalSelecionada() {
      this.getDados();
    },
    visaoGraficos() {
      this.getDados();
    },
    activatedTab(newVal) {
      if (newVal === 0) {
        this.getDados();
      }
    },
  },
  mounted() {
    this.getDados();
  },
  methods: {
    getDados() {
      if (this.activatedTab !== 0) return;

      this.$emit("updateLoadingDados", true);

      const promise =
        this.visaoGraficos === "acumulados"
          ? this.getDadosAcumulados()
          : this.getDadosMensais();

      promise
        .catch((err) => {
          console.error(err);
          this.$toast.error("Erro ao recuperar dados", "", {
            position: "topRight",
          });
        })
        .finally(() => this.$emit("updateLoadingDados", false));
    },
    getDadosAcumulados() {
      const competenciaSelecionada = this.competencia;
      this.loadingPorChave.dadosAcumulados = true;
      const anoAtual = dayjs(competenciaSelecionada).format("YYYY");
      const anoAnterior = dayjs(competenciaSelecionada)
        .subtract(1, "year")
        .format("YYYY");

      const competenciaDe = `${anoAnterior}-01`;
      const competenciaAte = `${anoAtual}-12`;
      return DashboardCobrancasIrregularidadesService.getDadosTOIsCobrancasPorInspecoesRealizadasAcumulados(
        competenciaDe,
        competenciaAte,
        this.regionalSelecionada,
      )
        .then((res) => {
          this.dadosAcumulados = res;
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => {
          this.loadingPorChave.dadosAcumulados = false;
        });
    },
    getDadosMensais() {
      const competenciaSelecionada = this.competencia;
      this.loadingPorChave.dadosMensais = true;
      const anoAtual = dayjs(competenciaSelecionada).format("YYYY");
      const anoAnterior = dayjs(competenciaSelecionada)
        .subtract(1, "year")
        .format("YYYY");

      const competenciaDe = `${anoAnterior}-01`;
      const competenciaAte = `${anoAtual}-12`;
      return DashboardCobrancasIrregularidadesService.getDadosTOIsCobrancasPorInspecoesRealizadasMensais(
        competenciaDe,
        competenciaAte,
        this.regionalSelecionada,
      )
        .then((res) => {
          this.dadosMensais = res;
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => {
          this.loadingPorChave.dadosMensais = false;
        });
    },
    criarGraficoGenerico({ series, tituloY, valueSuffix }) {
      return {
        chart: { type: "column" },
        title: { text: null },
        xAxis: {
          categories: this.dadosProcessados.categorias,
          title: { text: "Mês" },
        },
        yAxis: {
          title: { text: tituloY },
        },
        series: series,
        credits: { enabled: false },
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: [
                "viewFullscreen",
                "separator",
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
                "separator",
                "downloadCSV",
                "downloadXLS",
              ],
            },
          },
        },
        tooltip: valueSuffix ? { valueSuffix } : {},
        plotOptions: {
          column: {
            dataLabels: {
              enabled: false,
            },
          },
        },
      };
    },
  },
};
</script>

<style></style>
